"use client";

import React from 'react';
import Image from 'next/image';
import { 
  Users, 
  Shield, 
  TrendingUp,
  Building,
  FileText,
  Handshake,
  Award,
  Clock,
  Calculator,
  Star
} from 'lucide-react';
// import ConsultationModal from '@/components/public/ConsultationModal';
import PartnerCarousel from '@/components/PartnerCarousel';

export default function MortgagePage() {
  // const [isConsultationModalOpen, setIsConsultationModalOpen] = useState(false);
  // const openConsultationModal = () => setIsConsultationModalOpen(true);
  // const closeConsultationModal = () => setIsConsultationModalOpen(false);

  // Partner data for the carousel
  const partners = [
    { id: '1', name: 'Cross Homes', logo: '/assets/partners/cross-homes-logo.jpg' },
    { id: '2', name: 'Ashlar Homes', logo: '/assets/partners/ashlar-homes-logo.png' },
    { id: '3', name: 'Benchmark Homes', logo: '/assets/partners/benchmark-homes.jpg' },
    { id: '4', name: 'Benton Homes', logo: '/assets/partners/benton-logo.jpg' },
    { id: '5', name: 'Cannon Builders', logo: '/assets/partners/cannon-builders.jpg' },
    { id: '6', name: 'Claymont Development', logo: '/assets/partners/claymont-development.jpg' },
    { id: '7', name: 'CMS Homes', logo: '/assets/partners/cms-homes.jpg' },
    { id: '8', name: 'Consort Homes', logo: '/assets/partners/consort-homes.png' },
    { id: '9', name: 'Freeman Homes', logo: '/assets/partners/freeman-homes.jpg' },
    { id: '10', name: 'Hibbs Homes', logo: '/assets/partners/hibbs-homes.jpg' },
    { id: '11', name: 'Houston Homes', logo: '/assets/partners/houston-homes-logo.jpg' },
    { id: '12', name: 'Kemp Homes', logo: '/assets/partners/kemp-homes.jpg' },
    { id: '13', name: 'Lombardo Homes', logo: '/assets/partners/lombardo-gray.jpg' },
    { id: '14', name: 'McKelvey Homes', logo: '/assets/partners/mckelvey-homes.jpg' },
    { id: '15', name: 'Prestige Homes', logo: '/assets/partners/prestige-logo.jpg' },
    { id: '16', name: 'Renaissance Living', logo: '/assets/partners/renaissance-living.jpg' },
    { id: '17', name: 'Rolwes Homes', logo: '/assets/partners/rolwes-homes.jpg' },
    { id: '18', name: 'Rombach Homes', logo: '/assets/partners/rombach-homes.png' },
    { id: '19', name: 'TR Hughes Homes', logo: '/assets/partners/tr-hughes-homes.jpg' },
    { id: '20', name: 'UIC Homes', logo: '/assets/partners/uic-homes.jpg' },
    { id: '21', name: 'Vanderbilt Homes', logo: '/assets/partners/vanderbilt-homes.png' },
  ];

  return (
    <main className="flex min-h-screen flex-col">
      {/* Hero Section */}
      <section className="relative min-h-[400px] sm:min-h-[450px] md:h-[40vh] lg:h-[35vh] xl:h-[33vh] flex items-center justify-center overflow-hidden py-8 sm:py-12 md:py-0">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <Image
            src="/groundwork-hero.jpg"
            alt="New home construction"
            fill
            className="object-cover"
            priority
            sizes="100vw"
          />
          {/* Dark overlay for better text readability */}
          <div className="absolute inset-0 bg-black/40"></div>
        </div>
        
        {/* Content */}
        <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-4 sm:px-6">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4 leading-tight text-white">
            Groundwork Mortgage, a Builder Collaborative Company
          </h1>
          <p className="text-sm sm:text-base md:text-lg lg:text-xl mb-4 sm:mb-6 leading-relaxed text-white max-w-3xl mx-auto">
            We are an award-winning team specialized in supporting new home buyers and new home builders.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <button 
              onClick={() => window.open('https://1211416.my1003app.com/register', '_blank')}
              className="bg-white text-gray-900 px-5 py-2.5 sm:px-6 sm:py-3 rounded-lg font-semibold text-sm sm:text-base hover:bg-gray-100 transition-colors shadow-lg w-auto"
            >
              Apply Now
            </button>
            {/* <button className="border-2 border-white text-white px-6 py-3 rounded-lg font-semibold text-base hover:bg-white hover:text-gray-900 transition-colors">
              Learn More
            </button> */}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16" style={{ backgroundColor: '#0B253C' }}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-white">
              When Building A New Home, You Need The Right Mortgage Company.
            </h2>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-lg text-gray-300 mb-6">
                We repeatedly see new home buyers selecting lenders with insufficient new construction experience. 
                Like clockwork, they wind up frustrated and stressed by delayed contract deadlines, poor appraisals, 
                unexpected closing costs, higher interest rates, missed closing dates, and marathon closings.
              </p>
              <p className="text-lg text-gray-300 mb-6">
                Groundwork Mortgage is an authority in new construction purchase finance. We know new construction in the Midwest.
              </p>
              <p className="text-lg text-gray-300 mb-6">
                We have dedicated over 20+ years of residential construction and mortgage experience to serving the unique 
                needs of new home buyers and the home builders they choose to build their dream homes.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white/10 p-6 rounded-lg text-center">
                <Users className="w-12 h-12 text-white mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Expert Team</h3>
                <p className="text-gray-300 text-sm">20+ years of construction and mortgage experience</p>
              </div>
              <div className="bg-white/10 p-6 rounded-lg text-center">
                <Clock className="w-12 h-12 text-white mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">On-Time Closings</h3>
                <p className="text-gray-300 text-sm">Smooth, quick closings with no surprises</p>
              </div>
              <div className="bg-white/10 p-6 rounded-lg text-center">
                <Shield className="w-12 h-12 text-white mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Cost Savings</h3>
                <p className="text-gray-300 text-sm">Real cost savings for homebuyers</p>
              </div>
              <div className="bg-white/10 p-6 rounded-lg text-center">
                <TrendingUp className="w-12 h-12 text-white mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Proven Results</h3>
                <p className="text-gray-300 text-sm">5-Star Google Rating and HBA awards</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Process Section */}
      <section className="py-16 bg-muted/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4" style={{ color: '#0B253C' }}>
              Our Streamlined Process
            </h2>
            <p className="text-lg text-gray-600">
              From application to closing, we guide you every step of the way
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-primary" style={{ color: '#0B253C' }} />
              </div>
              <h3 className="text-lg font-semibold mb-2">1. Application</h3>
              <p className="text-sm text-gray-600">
                Quick and easy online application process tailored for new construction
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calculator className="w-8 h-8 text-primary" style={{ color: '#0B253C' }} />
              </div>
              <h3 className="text-lg font-semibold mb-2">2. Pre-Approval</h3>
              <p className="text-sm text-gray-600">
                Fast pre-approval with competitive rates and flexible terms
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building className="w-8 h-8 text-primary" style={{ color: '#0B253C' }} />
              </div>
              <h3 className="text-lg font-semibold mb-2">3. Construction</h3>
              <p className="text-sm text-gray-600">
                We work closely with your builder throughout the construction process
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Handshake className="w-8 h-8 text-primary" style={{ color: '#0B253C' }} />
              </div>
              <h3 className="text-lg font-semibold mb-2">4. Closing</h3>
              <p className="text-sm text-gray-600">
                Smooth closing process with no surprises or delays
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Awards Section */}
      <section className="py-16" style={{ backgroundColor: '#0B253C' }}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-white">
              Affiliated With Leading Industry Organizations
            </h2>
            <p className="text-lg text-gray-300">
              We maintain active membership and partnership with the most respected home building associations across the Midwest, ensuring we stay current with industry standards and best practices.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <Award className="w-16 h-16 text-white mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">HBA Awards</h3>
              <p className="text-gray-300">Multiple service awards from the Home Builders Association</p>
            </div>
            
            <div className="text-center">
              <Star className="w-16 h-16 text-white mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">5-Star Rating</h3>
              <p className="text-gray-300">Consistent 5-star Google reviews from satisfied customers</p>
            </div>
            
            <div className="text-center">
              <Users className="w-16 h-16 text-white mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Builder Recommended</h3>
              <p className="text-gray-300">Trusted by the best home builders in the Midwest</p>
            </div>
          </div>
          <div className="text-center mt-20">
            <p className="text-lg text-gray-300">
              Our involvement with these prestigious organizations allows us to network with industry leaders, stay ahead of regulatory changes, and bring the latest innovations to our clients. This commitment to professional development ensures you receive the most knowledgeable and up-to-date service available.
            </p>
          </div>
        </div>
      </section>

      {/* Trusted Partners Section */}
      <section className="py-8 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-6">
            <h2 className="text-3xl font-bold mb-4" style={{ color: '#0B253C' }}>
              Groundwork Mortgage is Proud to Have Served The Customers Of:
            </h2>
          </div>
          <PartnerCarousel items={partners} className="py-4" />
        </div>
      </section>

      {/* What Our Clients Say Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4" style={{ color: '#0B253C' }}>
              What Our Clients Say
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div className="text-center">
              <div className="text-6xl font-bold mb-2" style={{ color: '#0B253C' }}>5.0</div>
              <h3 className="text-xl font-semibold mb-2" style={{ color: '#0B253C' }}>Google Review Rating</h3>
              <p className="text-gray-700 text-sm">
                Consistently earning perfect ratings from satisfied clients
              </p>
            </div>
            
            <div className="text-center">
              <div className="text-6xl font-bold mb-2" style={{ color: '#0B253C' }}>20+</div>
              <h3 className="text-xl font-semibold mb-2" style={{ color: '#0B253C' }}>Years of Experience</h3>
              <p className="text-gray-700 text-sm">
                Decades of dedicated new construction expertise
              </p>
            </div>
            
            <div className="text-center">
              <div className="text-6xl font-bold mb-2" style={{ color: '#0B253C' }}>#1</div>
              <h3 className="text-xl font-semibold mb-2" style={{ color: '#0B253C' }}>New Home Lender</h3>
              <p className="text-gray-700 text-sm">
                More new home buyers trust us than any other lender
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center max-w-4xl mx-auto">
            <div className="flex justify-center">
              <Image
                src="/assets/logo/groundwork_company_of_the_year.JPG"
                alt="Home Builders Association Mortgage Company of the Year — seven consecutive years"
                width={576}
                height={580}
                className="w-full max-w-[326px] h-auto rounded-lg shadow-md"
              />
            </div>
            <div>
              <h3 className="text-2xl lg:text-3xl font-bold mb-4 leading-snug" style={{ color: '#0B253C' }}>
                Recognized by the people who build homes.
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed">
                Discover why the best home builders recommend Groundwork Mortgage and how we&apos;ve earned multiple service awards from the Home Builders Association. Our commitment to excellence shows in every closing and every satisfied client relationship.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-primary/10 to-secondary/20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4" style={{ color: '#0B253C' }}>
            Ready To Get Started?
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Contact us to discover why the best home builders recommend our team and how we earn 
            a Google Review 5-Star Rating, as well as multiple service awards from the Home Builders Association.
          </p>
          <div className="flex gap-4 justify-center">
            <button 
              onClick={() => window.open('https://1211416.my1003app.com/register', '_blank')}
              className="bg-primary text-primary-foreground px-8 py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity"
              style={{ backgroundColor: '#0B253C', color: 'white' }}
            >
              Apply Now
            </button>
            {/* <button className="border border-primary text-primary px-8 py-3 rounded-lg font-semibold hover:bg-primary/5 transition-colors">
              Get a Quote
            </button> */}
          </div>
          
          {/* <div className="mt-12 flex justify-center gap-8 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4" />
              <span>(636) 537-1005</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              <span>info@buildercollab.com</span>
            </div>
          </div> */}
        </div>
      </section>

      {/* Legal Disclaimer Footer */}
      <footer className="bg-white pt-8">
        <div className="container mx-auto px-4">
          <div className="border-t-4 mb-6" style={{ borderTopColor: '#0B253C' }}></div>
          <div className="text-xs leading-relaxed space-y-2" style={{ color: '#0B253C' }}>
            <p style={{ color: '#0B253C' }}>
              This is an advertisement. Information provided is strictly for advertising purposes and is not a commitment to lend or to extend credit. Credit availability is subject to property and credit approval, including, without limitation, loan application, verification and underwriting, and is only available to qualified applicant borrowers. Interest rates, if shown, vary and change without notice. Annual Percentage Rate (APR) will be higher than any interest rate shown. Payment amounts, if shown, are estimates only and any amounts shown for taxes, insurance or other escrow items are only estimates and actual payment obligations vary and will be higher. Financial and loan data shown, if any, is provided strictly for scenario and illustrative purposes. Mortgage insurance may be required depending upon an applicant borrower's actual scenario. Loan programs, availability, requirements, guidelines and credit criteria vary and change without notice.            </p>
            <p style={{ color: '#0B253C' }}>
              Framework Mortgage, LLC DBA Groundwork Mortgage is a Missouri licensed equal housing mortgage originator. To help applicable authorities fight the funding of terrorism and money laundering activities, law and regulations require that financial institutions like GWM obtain, verify and record information identifying each person applying for financing. When a consumer applies for financing GWM will ask each consumer applicant their legal name, address, date of birth and other identifying information, including asking for a copy of the consumer's driver's license or other identifying documents. Consumer understands that if consumer is purchasing a new home from a homebuilder ("Builder") that GWM advertises with, that the advertising business relationship is hereby fully disclosed to the consumer by GWM. Consumer further understands that consumer is NOT required to choose GWM for its mortgage origination services in order to purchase a home from a Builder nor as a condition of a Builder's sale of a home to consumer. It is consumer's sole choice whether to utilize GWM’s mortgage origination services. There are frequently other mortgage companies providing services similar to GWM. Consumers are encouraged to shop around to determine that they are receiving the best service and rates.            </p>
            <p style={{ color: '#0B253C' }}>
              GWM has a business relationship with Groundwork Insurance Services, LLC d/b/a Groundwork Insurance (“GWI”), a licensed insurance producer. GWM and GWI share common ownership by way of a parent limited liability company. Because of this relationship any referral from GWM to GWI or from GWI to GWM may provide GWM or GWI (as applicable) a financial or other benefit. The legal affiliation as between GWM and GWI is hereby fully disclosed to consumers. Consumers understand that consumers are NOT required to choose GWI for its insurance services as a condition of GWM’s mortgage origination services to consumer. There are frequently other insurance producers providing services similar to GWI. It is consumer's sole choice whether to utilize GWI’s insurance services. Consumers are encouraged to shop around to determine that they are receiving the best service and rates. This information is not a commitment to extend consumer credit as defined by section 226.2 Regulation Z.            </p>
            <div className="mt-4 pt-4 border-t border-gray-300">
              <p className="text-center" style={{ color: '#0B253C' }}>
                <strong>NMLS 1211416</strong>, Missouri mortgage company licensee 21-2198. 687 Trade Center Boulevard, Suite 120, Chesterfield MO 63005 (636) 537-1005. Additional registration and licensing information available via <a href="https://nmlsconsumeraccess.org/" className="underline hover:opacity-80" style={{ color: '#0B253C' }} target="_blank" rel="noopener noreferrer">NMLS Consumer Access</a>.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </main>
  );
}